import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IUser } from 'Model/index.js'
import type { OrganizationProfile } from '../../../Generated/OrganizationProfile.ts'
import type { IBulkResponse } from 'Framework/Repository/Response/IBulkResponse.js'
import type { OrganizationType } from 'Model/Organization/OrganizationType.js'
import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.js'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.js'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import { removeRequest } from 'Flux/FluxUtils.ts'
import { trackOrgAction } from 'Services/Telemetry/index.ts'

export interface IOrganizationState {
  openRequests: string[]
  organizations: Record<string, OrganizationProfile>
  users: Record<string, IUser>
  organizationMembers: Record<string, IUser[]>
  organizationsList: OrganizationProfile[]
  total: number
}

const initialState: IOrganizationState = {
  openRequests: [],
  organizations: {},
  users: {},
  organizationMembers: {},
  organizationsList: [],
  total: 0
}

const reducers = {
  getRelatedOrganizationsRequest: (state: Draft<IOrganizationState>, action: PayloadAction<unknown>) => {
    state.openRequests.push(action.type)
  },
  getRelatedOrganizationsSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<OrganizationProfile[]>) => {
    state.openRequests = removeRequest(state, action)
    action.payload.forEach((org) => {
      state.organizations[org.id] = org
    })
  },
  getRelatedOrganizationsFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  // Get Organization
  getOrganizationRequest: (state: Draft<IOrganizationState>, action: PayloadAction<{ organizationId: string }>) => {
    state.openRequests.push(action.type)
  },
  getOrganizationSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<{ organization: OrganizationProfile }>) => {
    state.organizations[action.payload.organization.id] = action.payload.organization
    state.openRequests = removeRequest(state, action)
  },
  getOrganizationFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // List Organizations
  listOrganizationsRequest: (state: Draft<IOrganizationState>, action: PayloadAction<{ page: number; size: number; filter: string }>) => {
    state.openRequests.push(action.type)
  },
  listOrganizationsSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<IBulkResponse<OrganizationProfile>>) => {
    state.organizationsList = action.payload.content ?? []
    state.total = action.payload.totalSize
    state.openRequests = removeRequest(state, action)
  },
  listOrganizationsFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // Get Organization Members
  getOrganizationMembersRequest: (state: Draft<IOrganizationState>, action: PayloadAction<{ organizationId: string }>) => {
    state.openRequests.push(action.type)
  },
  getOrganizationMembersSuccess: (
    state: Draft<IOrganizationState>,
    action: PayloadAction<{ organizationId: string; members: IUser[] }>
  ) => {
    state.organizationMembers[action.payload.organizationId] = action.payload.members
    state.openRequests = removeRequest(state, action)
  },
  getOrganizationMembersFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  deleteLogoRequest: (state: Draft<IOrganizationState>, action: PayloadAction<OrganizationProfile>) => {
    state.openRequests.push(action.type)
  },

  deleteLogoRequestSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<OrganizationProfile>) => {
    state.organizations[action.payload.id] = {
      ...state.organizations[action.payload.id],
      logo: undefined
    }
    state.openRequests = removeRequest(state, action)
  },

  deleteLogoRequestFailure: (state: Draft<IOrganizationState>, action: PayloadAction<OrganizationProfile>) => {
    state.openRequests = removeRequest(state, action)
  },

  updateLogoRequest: (state: Draft<IOrganizationState>, action: PayloadAction<{ id: string; logo: File }>) => {
    state.openRequests.push(action.type)
  },

  updateLogoRequestSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<{ orgId: string; logoKey: string }>) => {
    state.openRequests = removeRequest(state, action)
    state.organizations[action.payload.orgId] = {
      ...state.organizations[action.payload.orgId],
      logo: action.payload.logoKey
    }
  },

  updateLogoRequestFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // Update Organization Member
  updateOrganizationMemberRequest: (
    state: Draft<IOrganizationState>,
    action: PayloadAction<{
      organizationId: string
      memberId: string
      isAdmin?: boolean
      isContact?: boolean
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  updateOrganizationMemberSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<{ organizationId: string; user: IUser }>) => {
    state.users[action.payload.user.id] = action.payload.user

    // Update the member in the organization members list if it exists
    if (state.organizationMembers[action.payload.organizationId]) {
      const index = state.organizationMembers[action.payload.organizationId].findIndex((member) => member.id === action.payload.user.id)
      if (index !== -1) {
        state.organizationMembers[action.payload.organizationId][index] = action.payload.user
      }
    }

    trackOrgAction('member_role_changed', {
      org_id: action.payload.organizationId,
      is_admin: action.payload.user.roles?.includes('ORG_ADMIN') ?? false
    })
    state.openRequests = removeRequest(state, action)
  },
  updateOrganizationMemberFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // Update Organization
  updateOrganizationRequest: (
    state: Draft<IOrganizationState>,
    action: PayloadAction<{
      organizationId: string
      name: string
      isPublic: boolean
      description: string
      type: OrganizationType
      homepage?: string
      country?: string
      specialities?: string[]
      contactEmail?: string
      defaultSampleConfigurationType: SampleConfigurationTypeKey
      defaultRedlistMode: RedlistModeKey
      defaultProcessingType: ProcessingTypeKey
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  updateOrganizationSuccess: (state: Draft<IOrganizationState>, action: PayloadAction<{ organization: OrganizationProfile }>) => {
    state.organizations[action.payload.organization.id] = action.payload.organization

    // Update the organization in the organizations list if it exists
    const index = state.organizationsList.findIndex((org) => org.id === action.payload.organization.id)
    if (index !== -1) {
      state.organizationsList[index] = action.payload.organization
    }

    trackOrgAction('settings_updated', { org_id: action.payload.organization.id })
    state.openRequests = removeRequest(state, action)
  },
  updateOrganizationFailure: (state: Draft<IOrganizationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // Set organization and user (for caching)
  setOrganization: (state: Draft<IOrganizationState>, action: PayloadAction<{ id: string; organization: OrganizationProfile }>) => {
    state.organizations = { ...state.organizations, [action.payload.id]: action.payload.organization }
  },
  setUser: (state: Draft<IOrganizationState>, action: PayloadAction<{ id: string; user: IUser }>) => {
    state.users = { ...state.users, [action.payload.id]: action.payload.user }
  },

  reset: () => {
    return initialState
  }
}

export const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers
})
